// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import MyCustomQuotesDetailsPageTypeLink from "@bg/App/Components/MyCustomQuotes/MyCustomQuotesDetailsPageTypeLink";
import addQuoteTocart from "@bg/App/Handlers/CustomQuote/AddCustomQuoteToCart";
import deleteQuote from "@bg/App/Handlers/CustomQuote/DeleteCustomQuote";
import { CustomQuoteModel } from "@bg/App/Services/CustomQuoteService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}
interface State {
    quote?: CustomQuoteModel;
    deleteListModalIsOpen: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        quotes: state.myCustomQuotes.quotes,
        getCustomQuotesParameter: state.myCustomQuotes.getCustomQuotesParameter,
        language: state.context.session.language,
        addingToCart: state.myCustomQuoteDetails.addingToCart,
        rfqQuoteDetailsUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    deleteQuote,
    addQuoteTocart,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface MyListsListStyles {
    cardList?: InjectableCss;
    cardContainer?: InjectableCss;
    leftSection?: InjectableCss;
    basicInfoGridContainer?: InjectableCss;
    detailLinkGridItem?: InjectableCss;
    lastUpdatedText?: TypographyProps;
    createdUpdatedText?: TypographyProps;
    modifiedData?: InjectableCss;
    totalItems?: InjectableCss;
    actionsHidden?: InjectableCss;
    actionGridItem?: InjectableCss;
    actionAddToCartButton?: ButtonPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionsOverflowMenu?: InjectableCss;
    centeringWrapper?: InjectableCss;
    messageText?: TypographyProps;
    noQuotesFound?: InjectableCss;
    emptyMsg?: TypographyProps;
}

export const listStyles: MyListsListStyles = {
    cardList: {
        css: css`
            margin-top: 30px;
        `,
    },
    cardContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            padding: 15px 0;
            border-top: 2px solid ${getColor("common.ui3")};
            position: relative;
            &&:last-of-type {
                border-bottom: 2px solid ${getColor("common.ui3")};
            }
        `,
    },
    leftSection: {
        css: css`
            width: 50%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            @media (max-width: 768px) {
                width: 70%;
            }
            @media (max-width: 481px) {
                width: 100%;
                flex-wrap: wrap;
            }
        `,
    },
    basicInfoGridContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            max-width: 500px;
            width: 100%;
            @media (max-width: 1350px) {
                max-width: 350px;
            }
            @media (max-width: 600px) {
                max-width: 100%;
                margin-bottom: 15px;
            }
        `,
    },
    detailLinkGridItem: {
        css: css`
            font-size: ${fontConfig.fontSize.large}px;
            line-height: ${fontConfig.lineHeight.small};
            @media (max-width: 481px) {
                padding-right: 45px;
            }
        `,
    },
    lastUpdatedText: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: "20px",
    },
    createdUpdatedText: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: "20px",
        weight: fontConfig.weight.bold,
    },
    modifiedData: {
        css: css`
            display: flex;
            @media (max-width: 481px) {
                flex-direction: column;
            }
        `,
    },
    totalItems: {
        css: css`
            white-space: nowrap;
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    actionsHidden: {
        css: css`
            width: 250px;
            display: flex;
            align-items: center;
        `,
    },
    actionGridItem: {
        css: css`
            max-width: 250px;
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
        `,
    },
    actionAddToCartButton: {
        sizeVariant: "small",
        css: css`
            min-width: 198px;
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    actionDeleteButton: {
        css: css`
            &&&&& {
                background: transparent;
                width: auto;
                border: 0;
                box-shadow: none;
                min-height: auto;
                margin-left: auto;
                padding: 0;
                svg g {
                    fill: ${getColor("secondary")};
                }
                &&:hover,
                &&:focus {
                    svg g {
                        fill: ${getColor("primary")};
                    }
                }
            }
        `,
    },
    actionsOverflowMenu: {
        css: css`
            position: absolute;
            top: 15px;
            right: 0;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    messageText: {
        variant: "h4",
        css: css`
            margin: auto;
        `,
    },
    noQuotesFound: {
        css: css`
            text-align: center;
            padding: 20px;
            margin-top: 80px;
        `,
    },
    emptyMsg: {
        variant: "h4",
    },
};

const styles = listStyles;

class MyCustomQuotesList extends React.Component<Props, State> {
    static contextType = ToasterContext;
    context!: React.ContextType<typeof ToasterContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            deleteListModalIsOpen: false,
        };
    }

    deleteQuoteHandler = (quote: any) => {
        this.setState({
            quote,
            deleteListModalIsOpen: true,
        });
    };

    deleteCancelHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
    };

    deleteSubmitHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
        if (this.state.quote) {
            this.props.deleteQuote({
                quoteId: this.state.quote.id,
                onSuccess: this.onDeleteSuccess,
                onComplete(result: any) {
                    if (result.successful) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    onDeleteSuccess = () => {
        this.context.addToast({ body: translate("Quote Deleted"), messageType: "success" });
    };

    addQuoteToCart = (quote: CustomQuoteModel) => {
        this.props.addQuoteTocart({
            quoteId: quote.id,
            onSuccess: this.onAddToCartSuccess,
            onComplete(result: any) {
                if (result.successful) {
                    this.onSuccess?.();
                }
            },
        });
    };

    onAddToCartSuccess = () => {
        this.context.addToast({ body: translate("Quote Added to Cart"), messageType: "success" });
    };

    getModifiedOnDate = (date: string) => {
        const { language } = this.props;
        const updatedOnDisplay = getLocalizedDateTime({
            dateTime: new Date(date),
            language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
        return updatedOnDisplay;
    };

    returnToAssociatedQuote = (quote: CustomQuoteModel) => {
        const { rfqQuoteDetailsUrl } = this.props;
        if (rfqQuoteDetailsUrl && quote.quoteIdReference) {
            window.location.href = `${rfqQuoteDetailsUrl}?quoteId=${quote.quoteIdReference}`;
        }
    };

    checkIfUpdateOn = (quote: CustomQuoteModel) => {
        if (quote.createdOn === quote.modifiedOn) {
            return false;
        }
        return true;
    };

    render() {
        const { quotes, addingToCart } = this.props;
        if (!quotes || !quotes?.length) {
            return (
                <StyledWrapper {...styles.noQuotesFound}>
                    <Typography {...styles.emptyMsg}>{translate("No Quotes Found")}</Typography>
                </StyledWrapper>
            );
        }

        return (
            <StyledWrapper {...styles.cardList}>
                {quotes.map((quote: CustomQuoteModel) => (
                    <StyledWrapper key={quote.id} {...styles.cardContainer}>
                        <StyledWrapper {...styles.leftSection}>
                            <StyledWrapper
                                css={css`
                                    @media (max-width: 768px) {
                                        width: 100%;
                                    }
                                `}
                            >
                                <StyledWrapper {...styles.detailLinkGridItem}>
                                    <MyCustomQuotesDetailsPageTypeLink
                                        title={quote.name}
                                        quoteId={quote.id}
                                        testSelector="wishListCardName"
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.modifiedData}>
                                    <StyledWrapper>
                                        <Typography {...styles.createdUpdatedText}>
                                            {!this.checkIfUpdateOn(quote)
                                                ? translate("Created On:")
                                                : translate("Updated On:")}
                                            &nbsp;&nbsp;
                                        </Typography>
                                        <Typography
                                            data-test-selector="customQuoteLastUpdated"
                                            {...styles.lastUpdatedText}
                                        >
                                            {this.getModifiedOnDate(
                                                !this.checkIfUpdateOn(quote) ? quote.createdOn : quote.modifiedOn,
                                            )}
                                            &nbsp;
                                        </Typography>
                                    </StyledWrapper>
                                    {quote.modifiedBy && (
                                        <StyledWrapper>
                                            <Typography {...styles.createdUpdatedText}>
                                                {translate("By")}&nbsp;
                                            </Typography>

                                            <Typography
                                                data-test-selector="customQuoteUpdatedBy"
                                                {...styles.lastUpdatedText}
                                            >
                                                {quote.modifiedBy}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.totalItems}>
                                <Typography {...styles.createdUpdatedText}>
                                    {translate("Total Items:")}&nbsp;
                                </Typography>
                                <Typography {...styles.lastUpdatedText}>{quote.linesCount}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <Hidden below="md" {...styles.actionsHidden}>
                            <StyledWrapper {...styles.actionGridItem}>
                                {!quote.isCameFromNormalQuote && (
                                    <Button
                                        {...styles.actionAddToCartButton}
                                        onClick={() => this.addQuoteToCart(quote)}
                                        disabled={!quote.linesCount}
                                        data-test-selector={`customQuoteAddToCartButton_${quote.id}`}
                                    >
                                        {addingToCart.length && addingToCart === quote.id ? (
                                            <LoadingSpinner size={14} />
                                        ) : (
                                            translate("Add Quote To Cart")
                                        )}
                                    </Button>
                                )}
                                {quote.isCameFromNormalQuote && quote.allowRedirectToAssociatedQuote && (
                                    <Button
                                        {...styles.actionAddToCartButton}
                                        data-test-selector="returnToAssociatedQuote"
                                        onClick={() => this.returnToAssociatedQuote(quote)}
                                    >
                                        {translate("View Associated")}
                                    </Button>
                                )}

                                <Button
                                    {...styles.actionDeleteButton}
                                    onClick={() => this.deleteQuoteHandler(quote)}
                                    data-test-selector={`customQuoteDeleteButton_${quote.id}`}
                                >
                                    <ButtonIcon src="Delete" size={18} color="seconadry" />
                                </Button>
                            </StyledWrapper>
                        </Hidden>
                        <Hidden above="sm" {...styles.actionsOverflowMenu}>
                            <OverflowMenu position="end">
                                {!quote.isCameFromNormalQuote && (
                                    <Clickable
                                        disabled={!quote.linesCount || addingToCart.length > 0}
                                        onClick={() => this.addQuoteToCart(quote)}
                                    >
                                        {addingToCart.length && addingToCart === quote.id ? (
                                            <LoadingSpinner size={12} />
                                        ) : (
                                            translate("Add Quote To Cart")
                                        )}
                                    </Clickable>
                                )}

                                {quote.isCameFromNormalQuote && quote.allowRedirectToAssociatedQuote && (
                                    <Clickable
                                        data-test-selector="returnToAssociatedQuote"
                                        onClick={() => this.returnToAssociatedQuote(quote)}
                                    >
                                        {translate("View Associated Quote")}
                                    </Clickable>
                                )}

                                <Clickable onClick={() => this.deleteQuoteHandler(quote)}>
                                    {translate("Delete Quote")}
                                </Clickable>
                            </OverflowMenu>
                        </Hidden>
                    </StyledWrapper>
                ))}

                <TwoButtonModal
                    modalIsOpen={this.state.deleteListModalIsOpen}
                    headlineText={translate("Delete List")}
                    messageText={`${translate("Are you sure you want to delete")} ${
                        this.state.quote ? this.state.quote.name : ""
                    }?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="myListsListSubmitDelete"
                ></TwoButtonModal>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyCustomQuotesList),
    definition: {
        group: "My Custom Quotes" as any,
        displayName: "Quote List",
        allowedContexts: ["MyCustomQuotesPage" as any],
    },
};

export default widgetModule;
